import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Checkbox, Input } from 'semantic-ui-react'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import Layout from '~/components/Layout'
import i18n from '~/components/i18n'
import TableComponent from '~/components/TableComponent'
import { useDebounce } from 'use-lodash-debounce';
import ArticleBookingForm from './ArticleBookingForm'
import Dialog from '~/components/Dialog'
import notify from '~/components/Notify'
import { FarmersService } from '~/api/services/farmers/index'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import { El } from '~/components/Layout/style'
import EditArticleBooking from './EditArticleBooking'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { useAuth } from '~/routes/components/AuthProvider'
import { handleErrors } from '~/utils/helpers'
import ViewDialog from './ViewDialog'
import styled, { keyframes } from 'styled-components'

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Last booking'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'lastBooking'
  },
  {
    name: i18n.t('Date from'),
    key: 'dateFrom',
    format: 'DD.MM.YYYY',
    type: 'date'
  },
  {
    name: i18n.t('Article No.'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Amount'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Article name'),
    key: 'name'
  },
  {
    name: i18n.t('Unit'),
    key: 'unitLabel'
  },
  {
    name: i18n.t('Period'),
    key: 'intervalLabel'
  },
  {
    name: i18n.t('Article price'),
    key: 'netPrice',
    type: 'no-unit-number',
    decimalScale: 5
  },
  {
    name: i18n.t('Tax'),
    key: 'saleTaxLabel'
  },
  {
    name: i18n.t('Article price bruto (in €)'),
    key: 'grossPrice',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Actions')
  }
]

const getBookingTableHeaders = () => [
  {
    name: i18n.t('Booking date'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'bookingDate'
  },
  {
    name: i18n.t('Article No.'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Amount'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Article name'),
    key: 'name'
  },
  {
    name: i18n.t('Unit'),
    key: 'unitLabel'
  },
  {
    name: i18n.t('Period'),
    key: 'intervalLabel'
  },
  {
    name: i18n.t('Article price'),
    key: 'netPrice',
    type: 'no-unit-number',
    decimalScale: 5
  },
  {
    name: i18n.t('Tax'),
    key: 'tax',
    type: 'tax-number'
  },
  {
    name: i18n.t('Article price bruto (in €)'),
    key: 'grossPrice',
    type: 'no-unit-number'
  }
]

const getActions = () => [
  {
    popupMessage: i18n.t('Details'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'view'
  },
  {
    popupMessage: i18n.t('Edit article booking'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit'
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    color: 'red',
    actionType: 'remove'
  }
]

interface ArticleBookingsProps {
  farmer: string
  farmerData: any
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const DeleteBookingWrapper = styled.div`
animation: ${fadeIn} 0.3s ease-in;
`

const ArticleBookings: React.FC<ArticleBookingsProps> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { logged: { roleSlug } } } = useAuth()

  const [search, setSearch] = useState('')
  const [loadingUnsubscribe, setLoadingUnsubscribe] = useState(false)
  const [articleRemove, setArticleRemove] = useState(null)
  const [editArticleBookingId, setEditArticleBookingId] = useState(null)
  const [openModal, setOpenModal] = useState(false)
  const debouncedValue = useDebounce(search, 300);
  const [deleteBookings, setDeleteBookings] = useState(false)
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: '',
    month: ''
  })
  const [bookingArticleData, setBookingArticleData] = useState(null)

  const {
    data: articlesSettings,
    refetch: refetchSettings,
    isLoading: isBusySettings
  } = MilkMoneyQuery.getMilkMoneySettings(id)

  const {
    data: articles,
    isLoading: isBusy,
    refetch: fetchData,
    isFetching: isFetchingData
  } = FarmersQuery.getArticles(id, { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: bookings
  } = FarmersQuery.getBookings(
    id,
    articleRemove?.id,
    deleteBookings
  )

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  useEffect(() => {
    fetchData()
    refetchSettings()
  }, [t])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  function renderFilters() {
    return <Input
      icon={{ name: 'search', circular: true, link: true }}
      placeholder={t('Search...')}
      value={search}
      onChange={(e, data) => setSearch(data.value)}
    />
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'view':
        return setBookingArticleData(row)
      case 'remove':
        return setArticleRemove(row)
      case 'edit':
        return setEditArticleBookingId(row?.id)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchingData}
      animated
      actions={lightUserLogged ? [] : getActions()}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      tableColumnHeaders={getTableColumnHeaders()}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      rows={rows}
      total={filtered}
    />
  }

  async function handleRemoveArticle() {
    setLoadingUnsubscribe(true)
    try {
      await FarmersService.removeArticle(id, articleRemove?.id, { removeBookings: deleteBookings ? 1 : 0 })
      const articleName = `${articleRemove?.name} (${articleRemove?.uniqueId})`
      const companyData = `${props.farmerData.supplierNo} - ${props.farmerData.companyName}`
      notify(t('The article {{articleName}} was successfully deleted for the farmer {{companyData}}.', { articleName, companyData }), 'success')
      fetchData()
      setArticleRemove(null)
      setDeleteBookings(false)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingUnsubscribe(false)
  }

  function renderBookings() {
    return <TableComponent
      removePagination
      tableColumnHeaders={getBookingTableHeaders()}
      rows={bookings || []}
      total={bookings?.length || 0}
    />
  }

  function renderRemoveArticleFromFarmer() {
    return <Dialog
      isDialogOpen={Boolean(articleRemove)}
      textHeader={t('Remove article booking from farmer: {{farmer}}', { farmer: props.farmer })}
      style={{ minWidth: 'fit-content' }}
      textClose={t('Close')}
      isLoading={loadingUnsubscribe}
      handleClose={() => {
        setDeleteBookings(false)
        setArticleRemove(null)
      }}
      textContent={<>
        <Checkbox
          label={t('Delete bookings')}
          checked={deleteBookings}
          onChange={(e, data) => setDeleteBookings(data.checked)}
        />
        {deleteBookings && <DeleteBookingWrapper>{renderBookings()}</DeleteBookingWrapper>}
      </>}
      handleSubmit={() => handleRemoveArticle()}
      textSubmit={t('Delete')}
    />
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      onClick={() => setOpenModal(true)}
    >{t('Add new booking')}
    </El.PageHeaderActionButton>
  }

  function renderViewBooking() {
    return bookingArticleData && <ViewDialog
      articleBooking={bookingArticleData}
      handleClose={() => setBookingArticleData(null)}
      farmer={props.farmer}
      months={articlesSettings?.months || []}
    />
  }

  const { filtered, rows } = articles ? articles : { filtered: 0, rows: [] }
  const amount = rows.find(row => row?.id === editArticleBookingId)?.amount

  return <Layout
    isBusy={isBusy || isBusySettings}
    title={''}
    fontSizeHeader={'18px'}
    useSpinner
    childContainerSize={'auto'}
    filters={renderFilters()}
    actions={!lightUserLogged && renderActions()}
  >
    {openModal && <ArticleBookingForm
      farmer={props.farmer}
      openModal={openModal}
      farmerData={props.farmerData}
      startBookingDate={articlesSettings?.startMonth}
      handleClose={() => setOpenModal(false)}
      handleSubmit={() => {
        setOpenModal(null)
        fetchData()
      }}
    />}
    <EditArticleBooking
      articleBookingId={editArticleBookingId}
      handleClose={(submited) => {
        setEditArticleBookingId(false)
        if (submited) {
          fetchData()
        }
      }}
      farmer={props.farmer}
      startBookingDate={articlesSettings?.startMonth}
      amount={amount}
    />
    {renderRemoveArticleFromFarmer()}
    {renderViewBooking()}
    {renderTable()}
  </Layout>
}
export default ArticleBookings